import type { ComponentPropsWithRef } from 'react';

import { cn } from '../../../lib/cn';

export interface LabelProps extends ComponentPropsWithRef<'label'> {
  /**
   * Marks the field visually. This is presentation only — the source of truth
   * is the `required` prop on the control, which sets `aria-required`.
   */
  required?: boolean;
  /** Mutes the label when its control cannot be used. */
  disabled?: boolean;
}

/**
 * The shared label every input in the library renders on its own row, directly
 * above the control. It is a real `<label>` bound to the control with `htmlFor`,
 * so clicking the text focuses the field natively.
 */
export function Label({ required = false, disabled = false, className, children, ...rest }: LabelProps) {
  return (
    <label
      {...rest}
      className={cn(
        'flex items-center gap-1.5 font-display text-2xs font-semibold uppercase tracking-label',
        disabled ? 'text-muted' : 'text-heading',
        className,
      )}
    >
      {children}
      {required ? (
        // Decorative: `aria-required` on the control is what a screen reader
        // announces, so the marker stays out of the accessibility tree.
        <span aria-hidden="true" className="text-danger">
          *
        </span>
      ) : null}
    </label>
  );
}
